import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { setSidebarActive } from "../reducer/uiReducer";
import { useLogout } from "../hooks/useLogout";
import { Link, useLocation } from "react-router-dom";
import { Paths } from "../router/Paths";
import { NavType, setActiveNavTab } from "../reducer/uiReducer";

interface NavItemProps {
  name: string;
  path?: string;
  image: string;
  tab: NavType;
  subNav?: { name: string; path: string }[];
}

export default function Mainnav() {
  const dispatch = useDispatch();
  const logout = useLogout();
  const location = useLocation();

  const { headerShown, sidebarActive } = useSelector(
    (state: RootState) => state.ui
  );
  const { account } = useSelector((state: RootState) => state.account);

  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isSearchExpanded, setSearchExpanded] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        sidebarActive &&
        !target.closest(".sidebar") &&
        !target.closest(".profile")
      ) {
        dispatch(setSidebarActive(false));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dispatch, sidebarActive]);

  useEffect(() => {
    const pathToTab: { [key: string]: NavType } = {
      [Paths.welcome]: "home",
      [Paths.tourManagementCreate]: "create",
      [Paths.availablity]: "manage",
      [Paths.booking]: "booking",
      [Paths.finance]: "finance",
    };

    const currentTab = pathToTab[location.pathname];

    if (currentTab) {
      dispatch(setActiveNavTab(currentTab));
    }
    setMenuOpen(false);
  }, [location.pathname, dispatch]);

  const handleLogout = () => logout();

  return (
    <nav
      className={`${Classname.nav} ${headerShown ? "flex mb-10" : "hidden"}`}
    >
      <div className={Classname.navContainer}>
        {/* Logo */}
        <Link to={Paths.welcome}>
          <div className="logo">
            <img
              src="/logo.svg"
              alt="Logo"
              className="h-5 lg:h-9 object-contain"
            />
          </div>
        </Link>

        {/* Search bar and notification/profile (visible on larger screens) */}
        <div className="hidden lg:flex">
          {/* <div className={Classname.searchBar}>
            <button onClick={() => setSearchExpanded(!isSearchExpanded)} className={Classname.buttonHidden}>
              <svg className={Classname.svgIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <input
              type="text"
              placeholder="Search"
              className={`${Classname.inputSearch} ${isSearchExpanded ? "block" : "hidden md:block"}`}
            />
          </div> */}
          <div className={Classname.notificationProfile}>
            {/* <img src="/bellIcon.svg" alt="" /> */}
            <div
              className={Classname.profile}
              onClick={() => dispatch(setSidebarActive(!sidebarActive))}
            >
              <span>{account?.firstName[0]}</span>
              {sidebarActive && (
                <div className="absolute hidden lg:flex flex-col items-center shadow-md bg-white p-4 w-[300px] right-0 top-10 z-30">
                  <span className="text-black mb-2">
                    Hi, {account?.firstName} {account?.lastName}
                  </span>
                  <Link to={Paths.registration} className="text-black mb-2">
                    Company Profile
                  </Link>
                  <span
                    className="font-bold text-primary cursor-pointer"
                    onClick={handleLogout}
                  >
                    Logout
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu icon */}
        <button className="lg:hidden" onClick={() => setMenuOpen(true)}>
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* Sidebar for mobile */}
      {isMenuOpen && (
        <div className="fixed top-0 right-0 bottom-0 z-20 w-3/4 max-w-xs bg-white shadow-lg p-6 lg:hidden">
          {/* Close button */}
          <button
            className="absolute top-4 right-4"
            onClick={() => setMenuOpen(false)}
          >
            <svg
              className="h-6 w-6 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="mt-6 space-y-4">
            {/* User greeting */}
            <div className="flex items-center">
              <span className="block text-gray-800 font-medium">
                Hi, {account?.firstName} {account?.lastName}
              </span>
              <img src="/bellIcon.svg" alt="" />
            </div>
            {/* Nav Links */}
            <nav className={Classname.navItemContainer}>
              <NavItem
                image="/home.svg"
                name="Home"
                path={Paths.welcome}
                tab="home"
              />
              <NavItem
                image="/create.svg"
                name="Create"
                tab="create"
                subNav={[
                  { name: "New Product", path: Paths.tourManagementCreate },
                  { name: "Special Offer", path: "/create/special-offer" },
                  { name: "Protocol Service", path: "/create/protocol" },
                ]}
              />
              <NavItem
                image="/manage.svg"
                name="Manage"
                tab="manage"
                subNav={[
                  { name: "Availability", path: Paths.availablity },
                  { name: "Products", path: "/manage/product" },
                  { name: "Special Offers", path: "/manage/special-offer" },
                ]}
              />
              <NavItem
                image="/booking.svg"
                name="Bookings"
                tab="booking"
                subNav={[{ name: "Booking", path: Paths.booking }]}
              />
              <NavItem
                image="/home.svg"
                name="Finance"
                path={Paths.finance}
                tab="finance"
              />
              <NavItem
                image="/profile.svg"
                name="Company Profile"
                path={Paths.registration}
                tab="profile"
              />
            </nav>
          </div>
          <span
            className="block font-semibold text-blue-600 cursor-pointer text-center mt-4"
            onClick={handleLogout}
          >
            Logout
          </span>
        </div>
      )}
    </nav>
  );
}

const NavItem: React.FC<NavItemProps> = ({
  name,
  path,
  image,
  tab,
  subNav,
}: NavItemProps) => {
  const { activeNavTab } = useSelector((state: RootState) => state.ui);
  const isActive = activeNavTab === tab;
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if (subNav) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="w-full">
      <Link
        to={path || "#"}
        onClick={handleClick}
        className={`flex py-4 text-gray-500 text-md font-medium lg:flex gap-2 w-full ${
          isActive ? "border-b-4 border-blue-500" : ""
        }`}
      >
        <div className="justify-center flex">
          <img src={image} alt={name} className="h-5 w-5" />
        </div>
        <div className="flex-1">{name}</div>
        {subNav && (
          <svg
            className={`w-5 h-5 transform transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </Link>
      {subNav && isOpen && (
        <div className="pl-4 border-l-2 border-gray-200">
          {subNav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="block py-2 text-gray-600 hover:text-blue-600"
            >
              {item.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Classname = {
  nav: "bg-gray-100 mx-auto items-center justify-center w-full fixed top-0 left-0 right-0 z-20 mb-10",
  navContainer: " flex w-full max-w-7xl  justify-between  py-2 px-3 ",
  navItemContainer: " flex flex-col w-full max-w-7xl  justify-between  py-2 ",
  searchBar: "search-bar hidden lg:flex items-center bg-white p-2 rounded-full",
  buttonHidden: "lg:hidden",
  svgIcon: "h-5 w-5 text-gray-500",
  inputSearch: "ml-2 outline-none flex-1",
  notificationProfile: "flex gap-1 ml-2 items-center",
  profile:
    "profile flex flex-shrink-0 justify-center items-center h-8 w-8 rounded-full bg-blue-500 text-white relative cursor-pointer",
};
